import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d95r8e2h9roq6qfrc9a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage(serverMsg);
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch (_) {
      setFormStatus('error');
      setFormMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            <div>
              <p className="text-3xl md:text-4xl font-heading mb-3">
                Stay Updated
              </p>
              <p className="text-foreground-300 text-sm leading-relaxed mb-6 max-w-xs">
                Get the latest CSSBuy spreadsheet updates and new category additions delivered to your inbox.
              </p>
              <form
                className="flex flex-col sm:flex-row gap-3"
                onSubmit={handleSubscribe}
                data-readdy-form="d95r8e2h9roq6qfrc9a0"
              >
                <div className="honeypot-wrap">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent border-b border-foreground-600 text-sm text-background-50 placeholder-foreground-500 py-2 px-1 outline-none focus:border-primary-400 transition-colors"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-background-50 text-foreground-950 text-sm font-medium rounded-full hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                  <i className="ri-arrow-right-line ml-1.5"></i>
                </button>
              </form>
              {formMessage && (
                <p className={`mt-3 text-xs ${formStatus === 'success' ? 'text-primary-300' : 'text-red-400'}`}>
                  {formMessage}
                </p>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-5">
                Quick Links
              </p>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block text-sm text-foreground-300 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                >
                  Home
                </Link>
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/${article.slug}`}
                    className="block text-sm text-foreground-300 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                  >
                    {article.title}
                  </Link>
                ))}
                {categories.slice(0, 4).map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-sm text-foreground-300 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                  >
                    {cat.name} Spreadsheet
                  </a>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider mb-5">
                About
              </p>
              <p className="text-sm text-foreground-300 leading-relaxed mb-5">
                CSSSpreadsheet.online is an independent resource providing curated CSSBuy spreadsheet lists. We are not affiliated with CSSBuy, Kakobuy, Superbuy, or Oopbuy.
              </p>
              <p className="text-sm text-foreground-300">
                All spreadsheets are for informational purposes only. Always verify links before purchasing.
              </p>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
              >
                KASpreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
              >
                Buy It Now
              </a>
            </div>
            <p className="text-sm text-foreground-500">
              &copy; {new Date().getFullYear()} CSSSpreadsheet.online. All rights reserved.
            </p>
            <div className="flex items-center gap-5">
              <a href="#" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4">
                Privacy
              </a>
              <a href="#" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4">
                Terms
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}